/**
 * Upload Queue Constants
 */

import type { UploadQueueItemStatus } from './types';
import { S3UploadError } from '@/api/client';
import { isAccountMergedRetry } from '@/api/failureClass';
import {
  FALLBACK_UPLOAD_MAX_BYTES,
  fileTypeForFilename,
} from '@/features/files/uploadValidation';
import { formMessages } from '@/constants';
import { formatFileSize as formatBytes } from '@/utils/textFormat';
import { countLabel } from '@/lib/countLabel';

/** Threshold for considering a file "large" (50MB) */
const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024;

/** Threshold for showing upload time warning (100MB) */
export const WARNING_FILE_THRESHOLD = 100 * 1024 * 1024;

/** Maximum concurrent uploads (total) */
export const MAX_CONCURRENT = 5;

/** Maximum concurrent large file uploads */
export const MAX_LARGE_CONCURRENT = 2;

/** Maximum retry attempts for failed uploads */
export const MAX_RETRIES = 3;

/**
 * Audit L1: lifetime cap across user-initiated retries. The processor's
 * auto-retry loop uses MAX_RETRIES per pass; a user may re-trigger that
 * loop, but only until retryCount reaches this cap. Past it the Retry
 * button is disabled and retryItem surfaces RETRY_CAP_MESSAGE instead of
 * silently no-opping.
 */
export const RETRY_LIFETIME_CAP = MAX_RETRIES * 3;

/** Error message surfaced when the lifetime retry cap is reached */
export const RETRY_CAP_MESSAGE =
  'Too many retry attempts — remove the file and add it again';

/**
 * Statuses that count as "active" — in the queue and not finished.
 * Used for hasActiveUploads, queued-bytes accounting, and (audit M5a)
 * duplicate detection: re-adding a file only dedupes against active
 * items, so a completed or failed upload can be re-added.
 */
export const ACTIVE_STATUSES: readonly UploadQueueItemStatus[] = [
  'pending',
  'uploading',
  'paused',
  'uploaded',
  'processing',
];

/** Prefix for the transient duplicate-rejection notice (audit M5a) */
export const DUPLICATE_NOTICE_PREFIX = 'Duplicate skipped';

/** How long the duplicate-rejection notice stays on the item (ms) */
export const DUPLICATE_NOTICE_TIMEOUT = 5000;

/**
 * Transient, self-clearing notices ride on the item `error` field but get
 * warning (not failure) styling: the auto-retry countdown ("Retry 1/3 in
 * 2s") and the duplicate-skipped notice. Anything else in `error` is a
 * real failure.
 */
export function isTransientUploadNotice(message: string): boolean {
  return /^Retry \d+\//.test(message) || message.startsWith(DUPLICATE_NOTICE_PREFIX);
}

/** Base delay for exponential backoff (ms) */
const RETRY_BASE_DELAY = 1000;

/** Maximum delay between retries (ms) */
const RETRY_MAX_DELAY = 8000;

/** Estimated upload speed for time estimates (bytes/second) */
const ESTIMATED_UPLOAD_SPEED = 1 * 1024 * 1024; // 1 MB/s

/**
 * HTTP status codes that should trigger retry
 */
const RETRYABLE_STATUS_CODES = [
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
];

/**
 * S3 error codes (#84) worth another attempt. S3 answers a stalled or
 * throttled presigned POST with a **400/503 plus an XML code**, so the HTTP
 * status alone is not enough: `RequestTimeout` ("you stopped sending mid-body")
 * arrives as a 400, which the status table correctly treats as terminal for
 * policy failures. Retry on the code, not the status.
 *
 * Everything NOT listed here stays terminal — `EntityTooLarge`, `AccessDenied`,
 * and the policy-condition failures are deterministic: the same bytes with the
 * same presign will be refused identically, so retrying only wastes the user's
 * uplink and hides the real reason.
 */
const RETRYABLE_S3_CODES = [
  'RequestTimeout', // socket idle mid-body — the exact prod failure in #84
  'SlowDown', // throttling; back off and try again
  'InternalError', // S3's own transient 500
  'ServiceUnavailable',
];

// formatFileSize moved to @/utils/textFormat
export { formatFileSize } from '@/utils/textFormat';

/**
 * Estimate upload time for a file
 */
export function estimateUploadTime(bytes: number): string {
  const seconds = Math.ceil(bytes / ESTIMATED_UPLOAD_SPEED);
  if (seconds < 60) return countLabel(seconds, 'second', 'seconds');
  const minutes = Math.ceil(seconds / 60);
  return countLabel(minutes, 'minute', 'minutes');
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(retryCount: number): number {
  const delay = RETRY_BASE_DELAY * Math.pow(2, retryCount);
  return Math.min(delay, RETRY_MAX_DELAY);
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  // A rejected S3 POST (#84): S3's own code decides, not the HTTP status.
  if (error instanceof S3UploadError) {
    return (
      RETRYABLE_S3_CODES.includes(error.code) ||
      RETRYABLE_STATUS_CODES.includes(error.status)
    );
  }

  // Network errors
  if (error instanceof TypeError && error.message.includes('network')) {
    return true;
  }

  // AbortError or CanceledError should not be retried (user cancelled)
  if (error instanceof DOMException && error.name === 'AbortError') {
    return false;
  }
  if (error && typeof error === 'object' && 'code' in error && error.code === 'ERR_CANCELED') {
    return false;
  }

  // #1336 R4: a terminal `account_merged_retry` is a server error, as a 500 is.
  if (isAccountMergedRetry(error)) return true;

  // Axios error with status code
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response
  ) {
    const status = error.response.status as number;
    return RETRYABLE_STATUS_CODES.includes(status);
  }

  // Generic network error (no response)
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')
  ) {
    return true;
  }

  return false;
}

/**
 * User-facing text for an S3 rejection (#84).
 *
 * This is TERMINAL copy: the processor calls `getErrorMessage` only after the
 * retries are exhausted (a pending retry shows its own "Retry n/3 in Ns"
 * notice), so a transient code must not promise a retry that is no longer
 * coming — it tells the person what to do instead.
 *
 * - The transient codes (a stall, throttling, an S3 blip) all read as a stall:
 *   we tried, the connection is the suspect, try again.
 * - `EntityTooLarge` means the presign's content-length-range refused the
 *   file: speak the SAME sentence `validateUpload` produces at selection, so
 *   a cap read as a rule either way. (With selection-time validation now
 *   running on every path, reaching this means the server's cap is lower than
 *   the one the client mirrored — the copy still has to make sense.)
 * - Anything else: S3's own `<Message>`, never axios's generic string.
 */
function s3ErrorMessage(error: S3UploadError): string {
  if (RETRYABLE_S3_CODES.includes(error.code)) {
    return 'Upload stalled — check your connection and try again';
  }

  if (error.code === 'EntityTooLarge') {
    const type = fileTypeForFilename(`f.${error.fileExtension}`);
    return formMessages.upload.tooLarge(
      formatBytes(error.fileSize),
      formMessages.upload.typeLabels[type]!,
      formatBytes(FALLBACK_UPLOAD_MAX_BYTES[type]),
    );
  }

  // S3's own <Message> (or, when the body carried none, the synthesised
  // "Upload rejected by storage (...)" the API client built). Never axios's
  // generic status string — that is the string this bug is about.
  return error.message || `Upload rejected by storage (${error.code})`;
}

/**
 * Get user-friendly error message from an error
 */
export function getErrorMessage(error: unknown): string {
  // A rejected S3 POST carries the one string that explains it (#84). Axios
  // would otherwise surface "Request failed with status code 400", which told
  // neither the user nor us anything.
  if (error instanceof S3UploadError) {
    return s3ErrorMessage(error);
  }

  if (error instanceof Error) {
    // Replace technical messages with user-friendly ones
    const message = error.message.toLowerCase();
    if (message.includes('network') || message.includes('failed to fetch')) {
      return 'Network error';
    }
    if (message.includes('timeout') || message.includes('timed out')) {
      return 'Upload timed out';
    }
    if (message.includes('canceled') || message.includes('aborted')) {
      return 'Upload failed';
    }
    return error.message;
  }
  return 'Upload failed';
}

/**
 * Check if a file is considered "large"
 */
export function isLargeFile(size: number): boolean {
  return size >= LARGE_FILE_THRESHOLD;
}

/**
 * Generate a unique ID for queue items
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
